/* eslint-disable react/react-in-jsx-scope */
import IconExcel from "@/assets/icones/excel.svg?react";
import IconWord from "@/assets/icones/word.svg?react";
import verdeoHomeImage from "@/assets/images/verdeo-home.jpg";
import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import { toast } from "@/components/ui/use-toast";
import {
  AlertTriangle,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Clock3,
  Copy,
  Download,
  Eye,
  FileSpreadsheet,
  FileText,
  Loader,
  Mail,
  Pencil,
  RefreshCw,
  Send,
  Settings,
  Shapes,
  Share2,
  Trash,
  Unlock,
  UserPlus,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/auth/hooks/useAuth";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import { FileInput, FileUploader } from "@/components/extension/file-upload";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import CallFormActionDialog from "@/dashboard/components/subcontractor/CallFormActionDialog";
import GenerateNewLink from "@/dashboard/components/subcontractor/GenerateNewLink";
import { dashboardQueryKeys } from "@/dashboard/dashboardQueryConfig";
import { useFetchData } from "@/hooks/useFetchData";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import useImageCompressor from "@/hooks/useImageCompressor";
import usePostData from "@/hooks/usePostData";
import usePostMultipart from "@/hooks/usePostMultipart";
import { useTypeProjectContext } from "@/programs/context/typeProgramContext";
import { User } from "@/types";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const TableSkeleton = ({ columns = 4, rows = 3 }) => {
  return (
    <Table className="w-full border-collapse">
      <TableHeader>
        <TableRow>
          {[...Array(columns)].map((_, colIndex) => (
            <TableHead key={colIndex}>
              <Skeleton height={20} width="80%" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...Array(rows)].map((_, rowIndex) => (
          <TableRow key={rowIndex}>
            {[...Array(columns)].map((_, colIndex) => (
              <TableCell key={colIndex}>
                <Skeleton height={18} width="90%" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

type Intervenant = {
  id: string;
  nom: string;
  prenom: string;
  codeAcess: string;
  email: string;
  link: string;
  scoringEnabled?: boolean;
  project?: {
    id: number | null;
    name: string;
    status: number | null;
    programId?: number | string | null;
    isPersonnalized?: boolean;
    personnalized?: boolean;
    is_personnalized?: boolean;
    scoreLabel: string;
    scorePercentage?: number | null;
    hasPassedIntroduction?: boolean;
    submittedForScoring?: boolean;
  } | null;
};

type CallForProject = {
  id: number | string;
  name: string;
  introduction?: string;
  dateFin?: string | null;
  launchDate?: string | null;
  expectedDuration?: number | null;
  hasLaunchDate?: boolean;
  country?: {
    id: number;
    name: string;
  } | null;
  projectType?: {
    id: number;
    name: string;
  } | null;
  idProgramme: number | string;
  hasUsableForm?: boolean;
  formMode?: "global" | "call_specific";
  scoringEnabled?: boolean;
  useCallSpecificForm?: boolean;
  projectCount?: number;
  candidateCount?: number;
  statusCounts?: {
    validated: number;
    awaitingValidation: number;
    rejected: number;
    inProgress: number;
    notStarted: number;
  };
  canChangeForm?: boolean;
  intervenants: Intervenant[];
};

const normalizeCallForProject = (call: any): CallForProject => {
  if (
    call?.idProgramme !== undefined &&
    Array.isArray(call?.intervenants) &&
    call?.callProjectIntervenants === undefined
  ) {
    return {
      ...call,
      intervenants: call.intervenants,
    } as CallForProject;
  }

  return {
    id: call?.id,
    name: call.program?.name ?? call.name ?? "",
    introduction: call.program?.introduction ?? call?.introduction ?? "",
    dateFin: call?.dateFin ?? null,
    hasLaunchDate: Boolean(
      call?.hasLaunchDate ?? call?.launchDate ?? call?.expectedDuration,
    ),
    launchDate: call?.launchDate ?? null,
    expectedDuration:
      call?.expectedDuration !== undefined && call?.expectedDuration !== null
        ? Number(call.expectedDuration)
        : null,
    country: call?.country
      ? {
          id: Number(call.country.id),
          name: call.country.name ?? "",
        }
      : null,
    projectType: call?.projectType
      ? {
          id: Number(call.projectType.id ?? call.projectTypeId),
          name: call.projectType.name ?? "",
        }
      : null,
    idProgramme: call.program?.id ?? call.idProgramme ?? call.id,
    hasUsableForm: Boolean(call.hasUsableForm),
    formMode: call.formMode === "call_specific" ? "call_specific" : "global",
    scoringEnabled: Boolean(call.scoringEnabled),
    useCallSpecificForm: Boolean(call.useCallSpecificForm),
    projectCount: Number(call.projectCount ?? 0),
    candidateCount: Number(call.candidateCount ?? call.callProjectIntervenants?.length ?? 0),
    statusCounts: {
      validated: Number(call.statusCounts?.validated ?? 0),
      awaitingValidation: Number(call.statusCounts?.awaitingValidation ?? 0),
      rejected: Number(call.statusCounts?.rejected ?? 0),
      inProgress: Number(call.statusCounts?.inProgress ?? 0),
      notStarted: Number(call.statusCounts?.notStarted ?? 0),
    },
    canChangeForm: Boolean(call.canChangeForm),
    intervenants: (call.callProjectIntervenants ?? []).map((cpi: any) => ({
      id: String(cpi.id ?? ""),
      nom: cpi.intervenant?.lastname ?? "",
      prenom: cpi.intervenant?.firstname ?? "",
      email: cpi.intervenant?.email ?? "",
      codeAcess: cpi.codeAccess ?? "",
      scoringEnabled: Boolean(call.scoringEnabled),
      project: cpi.project
        ? {
            id: cpi.project.id ?? null,
            name: cpi.project.name ?? "",
            status: cpi.project.status ?? null,
            programId: call.program?.id ?? call.idProgramme ?? null,
            isPersonnalized: Boolean(cpi.project.isPersonnalized ?? cpi.project.personnalized ?? cpi.project.is_personnalized ?? false),
            personnalized: Boolean(cpi.project.isPersonnalized ?? cpi.project.personnalized ?? cpi.project.is_personnalized ?? false),
            is_personnalized: Boolean(cpi.project.isPersonnalized ?? cpi.project.personnalized ?? cpi.project.is_personnalized ?? false),
            scoreLabel: call.scoringEnabled ? (cpi.project.scoreLabel ?? "-") : "-",
            scorePercentage: call.scoringEnabled ? (cpi.project.scorePercentage ?? null) : null,
            hasPassedIntroduction: Boolean(cpi.project.hasPassedIntroduction),
            submittedForScoring: Boolean(cpi.project.submittedForScoring),
          }
        : null,
      link:
        window.location.origin +
        "/authentification-cfp/" +
        cpi.link +
        "/" +
        slugify(cpi.intervenant?.lastname + cpi.intervenant?.firstname),
    })) as Intervenant[],
  };
};

const buildCandidateProjectDashboardLink = (project: Intervenant["project"]): string => {
  if (!project?.id) return "#";

  const status = Number(project.status);
  const isPersonalized = Boolean(project.isPersonnalized ?? project.personnalized ?? project.is_personnalized ?? false);
  if (isPersonalized && Number.isFinite(status) && status !== 3) {
    const params = new URLSearchParams();
    params.set("projectId", String(project.id));
    if (project.programId) {
      params.set("programId", String(project.programId));
    }
    params.set("step", "1");

    return `/dashboard/project/create-custom?${params.toString()}`;
  }

  return `/dashboard/project/${project.id}?step=1`;
};

function slugify(str) {
  return str
    .normalize("NFD") // décompose les caractères accentués (ex: é devient e + accent)
    .replace(/[\u0300-\u036f]/g, "") // supprime les diacritiques (accents)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-") // remplace tout sauf lettres et chiffres par un tiret
    .replace(/^-+|-+$/g, ""); // supprime les tirets au début et à la fin
}

const getDefaultIntroImageSrc = (): string => verdeoHomeImage;

const isDefaultIntroImage = (value: string | null): boolean =>
  Boolean(
    value &&
    (value.includes("verdeo-home.jpg") || value.includes(verdeoHomeImage)),
  );

const escapeHtml = (value: string): string =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const buildDescriptionParagraphs = (description: string): string => {
  const blocks = description
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean);

  return blocks
    .map(
      (block) =>
        `<p style="margin:0 0 12px;font-size:16px;line-height:1.6;color:#374151;">${escapeHtml(block).replaceAll("\n", "<br />")}</p>`,
    )
    .join("");
};

const buildDefaultIntroductionContent = (
  programName: string,
  PPName: string,
  t: (key: string, options?: Record<string, unknown>) => string,
) => ({
  title:
    t(
      "Le porteur de projet {{ppName}} vous invite à répondre à l'appel à projets",
      {
        ppName: PPName,
      },
    ) + (programName ? ` ${programName}` : ""),
  description: [
    t(
      "Un porteur de projet vous a transmis ce lien afin de vous permettre de soumettre votre candidature dans le cadre de son appel à projets sur Verdeo.",
    ),
    t(
      "Pour accéder à cet appel à projets et déposer votre dossier, nous vous invitons à créer votre compte sur la plateforme. Vous pourrez ainsi présenter votre projet directement au porteur concerné.",
    ),
  ].join("\n\n"),
  imageSrc: getDefaultIntroImageSrc(),
});

const buildIntroductionHtml = ({
  imageSrc,
  title,
  description,
}: {
  imageSrc: string;
  title: string;
  description: string;
}) => `
  <div style="display:flex;flex-wrap:wrap;align-items:center;gap:24px;">
    <div style="flex:1 1 280px;">
      <img
        src="${imageSrc}"
        alt="intro"
        style="display:block;width:100%;max-width:100%;border-radius:12px;object-fit:cover;height:auto;"
      />
    </div>
    <div style="flex:1 1 360px;">
      <h2 style="margin:0 0 16px;font-size:24px;font-weight:700;line-height:1.3;">
        ${escapeHtml(title)}
      </h2>
      ${buildDescriptionParagraphs(description)}
    </div>
  </div>
`;

const normalizeDateOnly = (raw?: string | null): string | null => {
  if (!raw || !raw.trim()) return null;
  const direct = raw.trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(direct)) return direct;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isCallExpiredByDate = (raw?: string | null): boolean => {
  const deadline = normalizeDateOnly(raw);
  if (!deadline) return false;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate(),
  ).padStart(2, "0")}`;
  return today > deadline;
};

const CallStatusMiniCard = ({
  icon,
  value,
  label,
  className,
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
  className: string;
}) => (
  <div
    className={`flex h-[54px] w-[72px] flex-col justify-center rounded-[14px] px-2 py-1.5 ${className}`}
  >
    <div className="flex items-center gap-1.5">
      <div className="flex h-4 w-4 items-center justify-center">{icon}</div>
      <span className="text-[18px] font-semibold leading-none">{value}</span>
    </div>
    <p className="mt-0.5 text-[9px] font-medium leading-none opacity-90">
      {label}
    </p>
  </div>
);

const getDisplayedCandidateStatus = (
  project?: Intervenant["project"] | null,
  scoringEnabled?: boolean,
) => {
  if (!project?.id) return null;

  const rawStatus = Number(project.status ?? -1);
  if (scoringEnabled) {
    if (!project.hasPassedIntroduction) {
      return 0;
    }

    if (project.submittedForScoring && (rawStatus === 3 || rawStatus === 4 || rawStatus === 2 || rawStatus === 6)) {
      return rawStatus;
    }

    return 1;
  }

  if (rawStatus === 3 || rawStatus === 4 || rawStatus === 2 || rawStatus === 6) {
    return rawStatus;
  }

  return 1;
};

const getProjectStatusBadgeConfig = (
  status?: number | null,
  project?: Intervenant["project"] | null,
  scoringEnabled?: boolean,
) => {
  const effectiveStatus = getDisplayedCandidateStatus(project, scoringEnabled);

  switch (effectiveStatus ?? Number(status ?? -1)) {
    case 3:
      return {
        label: "Validé",
        className: "border-emerald-200 bg-emerald-50 text-emerald-700",
      };
    case 2:
    case 6:
      return {
        label: "En attente de validation",
        className: "border-sky-200 bg-sky-50 text-sky-700",
      };
    case 4:
      return {
        label: "Rejeté",
        className: "border-red-200 bg-red-50 text-red-700",
      };
    case 1:
      return {
        label: "En cours",
        className: "border-orange-200 bg-orange-50 text-orange-700",
      };
    case 0:
    case 7:
      return {
        label: "Non commencé",
        className: "border-slate-200 bg-white text-slate-600",
      };
    default:
      return {
        label: "Non commencé",
        className: "border-slate-200 bg-white text-slate-500",
      };
  }
};

const isAwaitingEvaluationScore = (
  project?: Intervenant["project"] | null,
): boolean => project?.scoreLabel === "En attente d'evaluation";

const formatCandidateScoreDisplay = (
  project?: Intervenant["project"] | null,
  scoringEnabled?: boolean,
) => {
  if (!scoringEnabled || !project) {
    return { label: "-", awaitingEvaluation: false };
  }

  if (isAwaitingEvaluationScore(project)) {
    const percentageLabel = project.scorePercentage !== null && project.scorePercentage !== undefined
      ? `${Number(project.scorePercentage).toFixed(0)}%`
      : "-";

    return {
      label: `${percentageLabel}`,
      awaitingEvaluation: true,
    };
    // return {
    //   label: `${percentageLabel} (AE)`,
    //   awaitingEvaluation: true,
    // };
  }

  return {
    label: project.scoreLabel ?? "-",
    awaitingEvaluation: false,
  };
};

const buildDisplayedStatusCounts = (
  intervenants: Intervenant[],
  scoringEnabled?: boolean,
) => {
  const counts = {
    validated: 0,
    awaitingValidation: 0,
    rejected: 0,
    inProgress: 0,
    notStarted: 0,
  };

  intervenants.forEach((intervenant) => {
    const status = getDisplayedCandidateStatus(intervenant.project, scoringEnabled);
    if (status === 3) {
      counts.validated += 1;
      return;
    }
    if (status === 2 || status === 6) {
      counts.awaitingValidation += 1;
      return;
    }
    if (status === 4) {
      counts.rejected += 1;
      return;
    }
    if (status === 1) {
      counts.inProgress += 1;
      return;
    }
    counts.notStarted += 1;
  });

  return counts;
};

const CandidateAccessCell = ({
  link,
  codeAccess,
}: {
  link: string;
  codeAccess: string;
}) => {
  const [copiedField, setCopiedField] = useState<"link" | "code" | null>(null);

  const handleCopy = async (value: string, field: "link" | "code") => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(
        () => setCopiedField((current) => (current === field ? null : current)),
        2000,
      );
    } catch (_err) {
      setCopiedField(null);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/80 bg-transparent px-2.5 py-1 text-xs font-medium text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50/70 hover:text-slate-700"
        >
          <Unlock className="h-4 w-4" />
          <span>Accès</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[380px] rounded-2xl border border-slate-200 p-0 shadow-xl"
      >
        <div className="rounded-t-2xl border-b border-slate-100 bg-[linear-gradient(180deg,#fbfdf9_0%,#f3f7ef_100%)] px-5 py-4">
          <p className="m-0 text-sm font-semibold text-slate-900">
            Accès candidat
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Retrouver rapidement le lien d’accès et le code du candidat.
          </p>
        </div>
        <div className="space-y-3 px-5 py-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div>
                <p className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Lien d'accès
                </p>
                <p className="mt-1 text-sm font-medium text-slate-800">
                  Accès direct au formulaire
                </p>
              </div>
              <button
                type="button"
                onClick={() => void handleCopy(link, "link")}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                {copiedField === "link" ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
                {copiedField === "link" ? "Copié" : "Copier"}
              </button>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 break-all">
              {link || "-"}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div>
                <p className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Code d'accès
                </p>
                <p className="mt-1 text-sm font-medium text-slate-800">
                  Code de connexion candidat
                </p>
              </div>
              <button
                type="button"
                onClick={() => void handleCopy(codeAccess, "code")}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                {copiedField === "code" ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
                {copiedField === "code" ? "Copié" : "Copier"}
              </button>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold tracking-[0.18em] text-slate-800 uppercase">
              {codeAccess || "-"}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const formatDisplayDate = (raw?: string | null) => {
  const normalized = normalizeDateOnly(raw);
  if (!normalized) return null;
  const [year, month, day] = normalized.split("-");
  if (!year || !month || !day) return null;
  return `${day}/${month}/${year}`;
};

const sanitizeAsciiFilename = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x20-\x7E]/g, "")
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

const getFileNameFromDisposition = (
  disposition: string | null,
  fallback: string,
) => {
  const asciiFallback = sanitizeAsciiFilename(fallback);
  if (!disposition) return asciiFallback;
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const simpleMatch = disposition.match(/filename="?([^"]+)"?/i);
  return simpleMatch?.[1] ?? asciiFallback;
};

const downloadCallForProjectExcel = async (callId: number) => {
  const token =
    localStorage.getItem("jwtToken") ?? sessionStorage.getItem("jwtToken");
  const response = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/call-for-project/${callId}/export/xlsx`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Erreur lors de l'export Excel");
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = getFileNameFromDisposition(
    response.headers.get("content-disposition"),
    `Rapport - App - export-${callId}.xlsx`,
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const downloadCallForProjectWord = async (callId: number) => {
  const token =
    localStorage.getItem("jwtToken") ?? sessionStorage.getItem("jwtToken");
  const response = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/call-for-project/${callId}/export/docx`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Erreur lors de l'export Word");
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = getFileNameFromDisposition(
    response.headers.get("content-disposition"),
    `Rapport - App - export-${callId}.docx`,
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const downloadGlobalCallForProjectsExcel = async () => {
  const token =
    localStorage.getItem("jwtToken") ?? sessionStorage.getItem("jwtToken");
  const response = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/call-for-project/export/global/xlsx`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Erreur lors de l'export global Excel");
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = getFileNameFromDisposition(
    response.headers.get("content-disposition"),
    "Rapport-app-export.xlsx",
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const downloadGlobalCallForProjectsWord = async () => {
  const token =
    localStorage.getItem("jwtToken") ?? sessionStorage.getItem("jwtToken");
  const response = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/call-for-project/export/global/docx`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Erreur lors de l'export global Word");
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = getFileNameFromDisposition(
    response.headers.get("content-disposition"),
    "Rapport-app-export.docx",
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const extractIntroductionContent = (
  html: string | undefined,
  fallback: { title: string; description: string; imageSrc: string },
) => {
  if (!html || html.trim() === "") return fallback;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const image = doc.querySelector("img");
    const title = doc.querySelector("h2");
    const descriptionNodes = Array.from(doc.querySelectorAll("p"));

    const parsedTitle = title?.textContent?.trim();
    const parsedDescription = descriptionNodes
      .map((node) => node.textContent?.trim())
      .filter((text): text is string => Boolean(text))
      .join("\n\n");
    const parsedImage =
      image?.getAttribute("src") || image?.getAttribute("data-mce-src");

    return {
      title: parsedTitle || fallback.title,
      description: parsedDescription || fallback.description,
      imageSrc:
        parsedImage && !isDefaultIntroImage(parsedImage)
          ? parsedImage
          : fallback.imageSrc,
    };
  } catch (_error) {
    return fallback;
  }
};

const ExportCallDialog = ({
  isOpen,
  onOpenChange,
  onExportExcel,
  onExportWord,
  isExporting,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onExportExcel: () => Promise<void>;
  onExportWord: () => Promise<void>;
  isExporting: boolean;
}) => {
  const [selectedFormat, setSelectedFormat] = useState<"xlsx" | "docx">("xlsx");

  useEffect(() => {
    if (isOpen) {
      setSelectedFormat("xlsx");
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-full min-w-0 max-w-[700px] rounded-[28px] border-[#d6e2d2] bg-[linear-gradient(180deg,#fbfdf9_0%,#f2f7ef_100%)] p-0 shadow-2xl sm:min-w-0">
        <div className="overflow-hidden rounded-[28px]">
          <div className="bg-[linear-gradient(135deg,#1f4b1d_0%,#5f8e53_60%,#94bf85_100%)] px-8 py-7 text-white">
            <div className="flex items-start justify-between gap-5">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/90">
                  <Download className="h-3.5 w-3.5" />
                  Export par appel à projets
                </div>
                <DialogTitle className="mt-4 text-[28px] font-semibold leading-tight text-white">
                  Exporter cet appel à projets
                </DialogTitle>
                <DialogDescription className="mt-2 max-w-[520px] text-sm leading-6 text-white/85">
                  Choisissez le format de sortie souhaité. L’utilisateur peut
                  exporter le rapport au format Word ou Excel selon ses besoins.
                </DialogDescription>
              </div>
              <div className="hidden rounded-[24px] border border-white/15 bg-white/10 p-4 md:block">
                <FileSpreadsheet className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>

          <div className="space-y-5 px-8 py-7">
            <div className="grid gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={() => setSelectedFormat("xlsx")}
                className={`group relative flex h-full min-h-[220px] flex-col overflow-hidden rounded-[24px] border p-5 text-left transition-all ${selectedFormat === "xlsx"
                  ? "border-[#6f9a5c] bg-white shadow-[0_18px_50px_rgba(75,107,60,0.15)] ring-2 ring-[#dfead9]"
                  : "border-[#d9e4d2] bg-white/80 hover:border-[#b7c9ae] hover:bg-white"
                  }`}
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#2e5f22_0%,#8fb37f_100%)]" />
                <div className="flex items-start justify-between gap-4">
                  <div className="rounded-[18px] bg-[#edf6e9] p-3 text-[#2a5b1e]">
                    <IconExcel className="h-8 w-8" />
                  </div>
                </div>
                <div className="mt-5 flex flex-1 flex-col">
                  <h3 className="text-lg font-semibold text-[#24451f]">
                    Excel
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-[#5d6e58]">
                    Export des données au format Excel afin de faciliter leur
                    consultation, leur tri, leur analyse et leur partage.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSelectedFormat("docx")}
                className={`group relative flex h-full min-h-[220px] flex-col overflow-hidden rounded-[24px] border p-5 text-left transition-all ${selectedFormat === "docx"
                  ? "border-[#8ba5d1] bg-white shadow-[0_18px_50px_rgba(86,112,163,0.12)] ring-2 ring-[#e4ebf8]"
                  : "border-[#d9e4d2] bg-white/80 hover:border-[#c8d5e8] hover:bg-white"
                  }`}
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#45638f_0%,#9ab4de_100%)]" />
                <div className="flex items-start justify-between gap-4">
                  <div className="rounded-[18px] bg-[#eef4ff] p-3 text-[#45638f]">
                    <IconWord className="h-8 w-8" />
                  </div>
                </div>
                <div className="mt-5 flex flex-1 flex-col">
                  <h3 className="text-lg font-semibold text-[#314664]">Word</h3>
                  <p className="mt-2 text-sm leading-6 text-[#66758a]">
                    Rapport simple avec les informations principales des programmes, les statuts des projets et l’URL.
                  </p>
                </div>
              </button>
            </div>

            <div className="rounded-[22px] border border-[#d7e3d2] bg-white/90 p-5">
              {selectedFormat === "xlsx" ? (
                <div className="flex items-start gap-4">
                  <div className="rounded-[16px] bg-[#edf6e9] p-3 text-[#2a5b1e]">
                    <FileSpreadsheet className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#294b24]">
                      Contenu de l'export Excel
                    </p>
                    <p className="mt-1 text-sm leading-6 text-[#5f6d5c]">
                      Un rapport avec une page de synthèse de l'appel et un
                      onglet candidats avec les projets, liens, statuts et
                      scores.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-4">
                  <div className="rounded-[16px] bg-[#eef4ff] p-3 text-[#45638f]">
                    <FileText className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#314664]">
                      Contenu de l'export Word
                    </p>
                    <p className="mt-1 text-sm leading-6 text-[#66758a]">
                      Le rapport reprend les informations de l’appel, puis la
                      liste détaillée des candidats.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-3 border-t border-[#dde7d8] bg-white/80 px-8 py-5 sm:justify-between sm:gap-3">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl border-[#cfdcc9] bg-white px-5 text-[#496045] hover:bg-[#f4f8f2]"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              className="rounded-xl bg-[linear-gradient(135deg,#21511c_0%,#5f8f51_100%)] px-6 text-white shadow-lg hover:opacity-95"
              onClick={() => {
                if (selectedFormat === "xlsx") {
                  void onExportExcel();
                } else {
                  void onExportWord();
                }
              }}
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <Loader className="mr-2 h-4 w-4 animate-spin" />
                  Export en cours
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Exporter
                </>
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const GlobalExportDialog = ({
  isOpen,
  onOpenChange,
  onExportExcel,
  onExportWord,
  isExporting,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onExportExcel: () => Promise<void>;
  onExportWord: () => Promise<void>;
  isExporting: boolean;
}) => {
  const [selectedFormat, setSelectedFormat] = useState<"xlsx" | "docx">("xlsx");

  useEffect(() => {
    if (isOpen) {
      setSelectedFormat("xlsx");
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-full min-w-0 max-w-[720px] rounded-[28px] border-[#d6e2d2] bg-[linear-gradient(180deg,#fbfdf9_0%,#f2f7ef_100%)] p-0 shadow-2xl sm:min-w-0">
        <div className="overflow-hidden rounded-[28px]">
          <div className="bg-[linear-gradient(135deg,#1f4b1d_0%,#5f8e53_60%,#94bf85_100%)] px-8 py-7 text-white">
            <div className="flex items-start justify-between gap-5">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/90">
                  <Download className="h-3.5 w-3.5" />
                  Rapport global de tous les appels à projets
                </div>
                <DialogTitle className="mt-4 text-[28px] font-semibold leading-tight text-white">
                  Exporter tous les appels à projets
                </DialogTitle>
                <DialogDescription className="mt-2 max-w-[560px] text-sm leading-6 text-white/85">
                  Génère un rapport consolidé de tous les appels à projets du
                  porteur avec les volumes de candidatures, les statuts, les
                  dates clés et les liens de partage.
                </DialogDescription>
              </div>
              <div className="hidden rounded-[24px] border border-white/15 bg-white/10 p-4 md:block">
                <BriefcaseBusiness className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>

          <div className="space-y-5 px-8 py-7">
            <div className="grid gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={() => setSelectedFormat("xlsx")}
                className={`relative overflow-hidden rounded-[24px] border p-5 text-left transition-all ${selectedFormat === "xlsx"
                  ? "border-[#6f9a5c] bg-white shadow-[0_18px_50px_rgba(75,107,60,0.15)] ring-2 ring-[#dfead9]"
                  : "border-[#d9e4d2] bg-white/80 hover:border-[#b7c9ae] hover:bg-white"
                  }`}
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#2e5f22_0%,#8fb37f_100%)]" />
                <div className="flex items-start justify-between gap-4">
                  <div className="rounded-[18px] bg-[#edf6e9] p-3 text-[#2a5b1e]">
                    <IconExcel className="h-8 w-8" />
                  </div>
                </div>
                <h3 className="mt-5 text-lg font-semibold text-[#24451f]">
                  Excel
                </h3>
                <p className="mt-2 text-sm leading-6 text-[#5d6e58]">
                  Export des données au format Excel afin de faciliter leur
                  consultation, leur tri, leur analyse et leur partage.
                </p>
              </button>

              <button
                type="button"
                onClick={() => setSelectedFormat("docx")}
                className={`relative overflow-hidden rounded-[24px] border p-5 text-left transition-all ${selectedFormat === "docx"
                  ? "border-[#8ba5d1] bg-white shadow-[0_18px_50px_rgba(86,112,163,0.12)] ring-2 ring-[#e4ebf8]"
                  : "border-[#d9e4d2] bg-white/80 hover:border-[#c8d5e8] hover:bg-white"
                  }`}
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#45638f_0%,#9ab4de_100%)]" />
                <div className="flex items-start justify-between gap-4">
                  <div className="rounded-[18px] bg-[#eef4ff] p-3 text-[#45638f]">
                    <IconWord className="h-8 w-8" />
                  </div>
                </div>
                <h3 className="mt-5 text-lg font-semibold text-[#314664]">
                  Word
                </h3>
                <p className="mt-2 text-sm leading-6 text-[#66758a]">
                  Rapport simple avec les informations principales des programmes, les statuts des projets et l’URL.
                </p>
              </button>
            </div>
          </div>

          <DialogFooter className="gap-3 border-t border-[#dde7d8] bg-white/80 px-8 py-5 sm:justify-between sm:gap-3">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl border-[#cfdcc9] bg-white px-5 text-[#496045] hover:bg-[#f4f8f2]"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              className="rounded-xl bg-[linear-gradient(135deg,#21511c_0%,#5f8f51_100%)] px-6 text-white shadow-lg hover:opacity-95"
              onClick={() => {
                if (selectedFormat === "xlsx") {
                  void onExportExcel();
                } else {
                  void onExportWord();
                }
              }}
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <Loader className="mr-2 h-4 w-4 animate-spin" />
                  Export en cours
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Exporter
                </>
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EditProgramDialog = ({
  isOpen,
  onOpenChange,
  programm,
  onSave,
  isSaving,
  t,
  user,
  typesProject,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  programm: CallForProject;
  onSave: (
    newName: string,
    intro: string,
    projectTypeId: number | null,
    newDateFin: string,
    hasLaunchDate: boolean,
    newLaunchDate: string | null,
    newExpectedDuration: number | null,
    newCountryId: number | null,
  ) => Promise<boolean>;
  isSaving: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
  user: any;
  typesProject: any[];
}) => {
  const formatDateForInput = (value?: string | null) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
  };
  const defaultIntroContent = buildDefaultIntroductionContent(
    programm?.name || "",
    `${user?.firstname ?? ""} ${user?.lastname ?? ""}`.trim(),
    t,
  );
  const introContent = extractIntroductionContent(
    programm?.introduction,
    defaultIntroContent,
  );

  const [newName, setNewName] = useState(programm?.name || "");
  const [errorProjectType, setErrorProjectType] = useState(false);
  const [errorDateFin, setErrorDateFin] = useState(false);
  const [errorLaunchDate, setErrorLaunchDate] = useState(false);
  const [errorExpectedDuration, setErrorExpectedDuration] = useState(false);
  const [errorCountry, setErrorCountry] = useState(false);
  const [projectTypeSearchOpen, setProjectTypeSearchOpen] = useState(false);
  const [countrySearchOpen, setCountrySearchOpen] = useState(false);
  const [selectedProjectTypeId, setSelectedProjectTypeId] = useState<string>(
    programm?.projectType?.id ? String(programm.projectType.id) : "",
  );
  const [newDateFin, setNewDateFin] = useState(
    formatDateForInput(programm?.dateFin),
  );
  const [hasLaunchDate, setHasLaunchDate] = useState(
    Boolean(
      programm?.hasLaunchDate ??
      programm?.launchDate ??
      programm?.expectedDuration,
    ),
  );
  const [newLaunchDate, setNewLaunchDate] = useState(
    formatDateForInput(programm?.launchDate),
  );
  const [newExpectedDuration, setNewExpectedDuration] = useState(
    programm?.expectedDuration ? String(programm.expectedDuration) : "",
  );
  const [selectedCountryId, setSelectedCountryId] = useState<string>(
    programm?.country?.id ? String(programm.country.id) : "",
  );
  const [introTitle, setIntroTitle] = useState(introContent.title);
  const [introDescription, setIntroDescription] = useState(
    introContent.description,
  );
  const [introImage, setIntroImage] = useState(introContent.imageSrc);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const todayDate = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const { postMultipart } = usePostMultipart();
  const { handleCompressImage } = useImageCompressor();
  const countries = useFetchData<any[]>(
    `${import.meta.env.VITE_API_ENDPOINT}/countries`,
  );

  const projectTypeOptions = (Array.isArray(typesProject) ? typesProject : [])
    .map((item: any) => ({
      id: String(item?.id ?? ""),
      label:
        item?.name ||
        item?.projectTypeLangs?.find((entry: any) => entry?.lang?.code === "fr")
          ?.name ||
        item?.projectTypeLangs?.[0]?.name ||
        `Type #${item?.id}`,
    }))
    .filter((item: any) => item.id);

  const selectedProjectTypeLabel =
    projectTypeOptions.find(
      (option: any) => option.id === selectedProjectTypeId,
    )?.label ?? "Selectionner un type de projet";
  const countryOptions = (
    Array.isArray(countries.data)
      ? countries.data
      : Array.isArray((countries.data as any)?.["hydra:member"])
        ? (countries.data as any)["hydra:member"]
        : []
  )
    .map((country: any) => ({
      id: String(country?.id ?? ""),
      label:
        country?.countryLang?.find((entry: any) => entry?.lang?.code === "fr")
          ?.name ||
        country?.name ||
        `Pays #${country?.id}`,
    }))
    .filter((item: any) => item.id);

  const selectedCountryLabel =
    countryOptions.find((option: any) => option.id === selectedCountryId)
      ?.label ?? "Sélectionner un pays";

  useEffect(() => {
    const nextDefault = buildDefaultIntroductionContent(
      programm?.name || "",
      `${user?.firstname ?? ""} ${user?.lastname ?? ""}`.trim(),
      t,
    );
    const parsed = extractIntroductionContent(
      programm?.introduction,
      nextDefault,
    );
    setNewName(programm?.name || "");
    setErrorProjectType(false);
    setErrorDateFin(false);
    setErrorLaunchDate(false);
    setErrorExpectedDuration(false);
    setErrorCountry(false);
    setSelectedProjectTypeId(
      programm?.projectType?.id ? String(programm.projectType.id) : "",
    );
    setNewDateFin(formatDateForInput(programm?.dateFin));
    setHasLaunchDate(
      Boolean(
        programm?.hasLaunchDate ??
        programm?.launchDate ??
        programm?.expectedDuration,
      ),
    );
    setNewLaunchDate(formatDateForInput(programm?.launchDate));
    setNewExpectedDuration(
      programm?.expectedDuration ? String(programm.expectedDuration) : "",
    );
    setSelectedCountryId(
      programm?.country?.id ? String(programm.country.id) : "",
    );
    setIntroTitle(parsed.title);
    setIntroDescription(parsed.description);
    setIntroImage(parsed.imageSrc);
  }, [isOpen, programm?.id]);

  useEffect(() => {
    if (selectedProjectTypeId || !programm?.projectType?.name) return;
    const matched = projectTypeOptions.find(
      (option: any) =>
        String(option.label || "")
          .trim()
          .toLowerCase() ===
        String(programm.projectType?.name || "")
          .trim()
          .toLowerCase(),
    );
    if (matched?.id) {
      setSelectedProjectTypeId(String(matched.id));
    }
  }, [selectedProjectTypeId, projectTypeOptions, programm?.projectType?.name]);
  const handleIntroImageChange = async (files: File[]) => {
    if (!files || files.length === 0) return;
    setIsUploadingImage(true);

    const data = new FormData();
    const originalFile = files[0];
    let imageFile: File | Blob = originalFile;
    if (originalFile?.type?.startsWith("image/")) {
      const compressedBlob = await handleCompressImage(
        originalFile,
        1600,
        1600,
        0.9,
      );
      if (compressedBlob) {
        imageFile = new File([compressedBlob], originalFile.name, {
          type: compressedBlob.type || originalFile.type,
        });
      }
    }
    data.append("type", "8");
    if (imageFile) data.append("files[]", imageFile);

    const newMedias = await postMultipart({
      url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
      data,
    });

    if (
      !Array.isArray(newMedias) &&
      newMedias?.success !== undefined &&
      !newMedias.success
    ) {
      toast({ title: newMedias.message, duration: 1000 });
      setIsUploadingImage(false);
      return;
    }

    const media = Array.isArray(newMedias) ? newMedias[0] : null;
    if (media?.uri) {
      setIntroImage(
        String(media.uri).startsWith("http")
          ? media.uri
          : `${import.meta.env.VITE_MEDIA_BASE}${media.uri}`,
      );
    }
    setIsUploadingImage(false);
  };

  const handleSave = async () => {
    if (!selectedProjectTypeId) {
      setErrorProjectType(true);
      return;
    }
    if (!newDateFin) {
      setErrorDateFin(true);
      return;
    }
    if (hasLaunchDate && !newLaunchDate) {
      setErrorLaunchDate(true);
      return;
    }
    if (
      hasLaunchDate &&
      (!newExpectedDuration || Number(newExpectedDuration) <= 0)
    ) {
      setErrorExpectedDuration(true);
      return;
    }
    if (!selectedCountryId) {
      setErrorCountry(true);
      return;
    }

    const newIntro = buildIntroductionHtml({
      imageSrc: introImage,
      title: introTitle,
      description: introDescription,
    });
    const parsedProjectTypeId = selectedProjectTypeId
      ? Number(selectedProjectTypeId)
      : null;
    const normalizedProjectTypeId =
      typeof parsedProjectTypeId === "number" &&
        Number.isFinite(parsedProjectTypeId)
        ? parsedProjectTypeId
        : null;
    const ok = await onSave(
      newName.trim(),
      newIntro,
      normalizedProjectTypeId,
      newDateFin,
      hasLaunchDate,
      hasLaunchDate ? newLaunchDate : null,
      hasLaunchDate ? Number(newExpectedDuration) : null,
      Number(selectedCountryId),
    );
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog modal={true} open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        id="edit-program-dialog-content"
        className="z-[1200] flex max-h-[90vh] w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-0 shadow-xl sm:max-w-[760px]"
        onInteractOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onFocusOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="shrink-0 border-b border-slate-200 px-6 py-5">
          <DialogTitle className="text-xl font-semibold text-slate-900">
            Modifier le programme
          </DialogTitle>
          <DialogDescription className="mt-1 text-sm text-slate-600">
            Mettez a jour le nom et l&apos;introduction de votre appel a projet.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 space-y-5 overflow-y-auto px-6 py-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Nom du programme
            </label>
            <Input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Ex: Programme innovation sociale 2026"
              className="h-11 border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:ring-slate-400"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Type de projet
              </label>
              <Popover
                open={projectTypeSearchOpen}
                onOpenChange={setProjectTypeSearchOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    type="button"
                    role="combobox"
                    className="h-11 w-full justify-between border-slate-300 text-left text-black hover:bg-white hover:text-black hover:border-[#1d5214]"
                  >
                    <span className="truncate">{selectedProjectTypeLabel}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                  onWheelCapture={(event) => event.stopPropagation()}
                >
                  <Command className="max-h-64 overflow-hidden">
                    <CommandInput placeholder="Rechercher un type..." />
                    <CommandList className="max-h-52 overflow-y-auto overscroll-contain">
                      <CommandEmpty>Aucun type de projet</CommandEmpty>
                      <CommandGroup>
                        {projectTypeOptions.map((option: any) => (
                          <CommandItem
                            key={option.id}
                            value={`${option.id} ${option.label}`}
                            onSelect={() => {
                              setSelectedProjectTypeId(option.id);
                              setErrorProjectType(false);
                              setProjectTypeSearchOpen(false);
                            }}
                            className="text-black hover:!bg-white hover:!text-black data-[selected='true']:!bg-white data-[selected='true']:!text-black data-[selected=true]:!bg-white data-[selected=true]:!text-black aria-selected:!bg-white aria-selected:!text-black"
                          >
                            <Check
                              className={`mr-2 h-4 w-4 ${selectedProjectTypeId === option.id
                                ? "opacity-100"
                                : "opacity-0"
                                }`}
                            />
                            <span className="truncate">{option.label}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {errorProjectType && (
                <p className="text-sm text-red-500">Champ obligatoire</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Date limite de dépôt de candidature
              </label>
              <Input
                type="date"
                min={todayDate}
                value={newDateFin}
                onChange={(e) => {
                  setNewDateFin(e.target.value);
                  setErrorDateFin(false);
                }}
                className="h-11 border-slate-300 text-slate-900 focus-visible:ring-slate-400"
              />
              {errorDateFin && (
                <p className="text-sm text-red-500">Champ obligatoire</p>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <label
                  htmlFor="edit-call-has-launch-date"
                  className="text-sm font-medium text-slate-700"
                >
                  Définir une date de lancement
                </label>
                <p className="mt-1 text-xs text-slate-500">
                  Activez cette option pour renseigner la date de lancement et
                  la durée prévue du programme.
                </p>
              </div>
              <Switch
                id="edit-call-has-launch-date"
                checked={hasLaunchDate}
                onCheckedChange={(checked) => {
                  setHasLaunchDate(checked);
                  setErrorLaunchDate(false);
                  setErrorExpectedDuration(false);
                  if (!checked) {
                    setNewLaunchDate("");
                    setNewExpectedDuration("");
                  }
                }}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {hasLaunchDate && (
              <>
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Date de lancement
                  </label>
                  <Input
                    type="date"
                    value={newLaunchDate}
                    onChange={(e) => {
                      setNewLaunchDate(e.target.value);
                      setErrorLaunchDate(false);
                    }}
                    className="h-11 border-slate-300 text-slate-900 focus-visible:ring-slate-400"
                  />
                  {errorLaunchDate && (
                    <p className="text-sm text-red-500">Champ obligatoire</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Durée prévue
                  </label>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={newExpectedDuration}
                    onChange={(e) => {
                      setNewExpectedDuration(e.target.value);
                      setErrorExpectedDuration(false);
                    }}
                    placeholder="Durée en mois"
                    className="h-11 border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:ring-slate-400"
                  />
                  {errorExpectedDuration && (
                    <p className="text-sm text-red-500">
                      Renseignez une durée supérieure à 0.
                    </p>
                  )}
                </div>
              </>
            )}

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Pays
              </label>
              <Popover
                open={countrySearchOpen}
                onOpenChange={setCountrySearchOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    type="button"
                    role="combobox"
                    className="h-11 w-full justify-between border-slate-300 text-left text-black hover:bg-white hover:text-black hover:border-[#1d5214]"
                  >
                    <span className="truncate">{selectedCountryLabel}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                  onWheelCapture={(event) => event.stopPropagation()}
                >
                  <Command className="max-h-64 overflow-hidden">
                    <CommandInput placeholder="Rechercher un pays..." />
                    <CommandList className="max-h-52 overflow-y-auto overscroll-contain">
                      <CommandEmpty>Aucun pays</CommandEmpty>
                      <CommandGroup>
                        {countryOptions.map((option: any) => (
                          <CommandItem
                            key={option.id}
                            value={`${option.id} ${option.label}`}
                            onSelect={() => {
                              setSelectedCountryId(option.id);
                              setErrorCountry(false);
                              setCountrySearchOpen(false);
                            }}
                            className="text-black hover:!bg-white hover:!text-black data-[selected='true']:!bg-white data-[selected='true']:!text-black data-[selected=true]:!bg-white data-[selected=true]:!text-black aria-selected:!bg-white aria-selected:!text-black"
                          >
                            <Check
                              className={`mr-2 h-4 w-4 ${selectedCountryId === option.id
                                ? "opacity-100"
                                : "opacity-0"
                                }`}
                            />
                            <span className="truncate">{option.label}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {errorCountry && (
                <p className="text-sm text-red-500">Champ obligatoire</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex flex-row gap-4 items-center text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
              <p className="m-0">Landing page</p>
              <Popover>
                <PopoverTrigger asChild>
                  <InfoCircledIcon className="cursor-pointer" />
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  Cette page est la première chose visible lorsqu’un utilisateur
                  accède à l’appel à projet.
                </PopoverContent>
              </Popover>
            </div>
            <div className="mt-4 space-y-4 flex flex-row gap-4">
              <div className="w-2/3">
                <FileUploader
                  value={[]}
                  onValueChange={handleIntroImageChange}
                  dropzoneOptions={{
                    multiple: false,
                    maxSize: 1024 * 1024 * 15,
                    accept: { "image/*": [".jpg", ".jpeg", ".png", ".webp"] },
                  }}
                  className="w-full"
                >
                  <FileInput className="w-full rounded-lg border border-dashed border-slate-300 p-3">
                    <div className="flex min-h-[180px] items-center justify-center overflow-hidden rounded-md bg-white">
                      {isUploadingImage ? (
                        <Loader className="h-6 w-6 animate-spin text-slate-500" />
                      ) : (
                        <img
                          src={introImage}
                          alt="Aperçu introduction"
                          className="max-h-[220px] w-full rounded-md object-cover"
                        />
                      )}
                    </div>
                  </FileInput>
                </FileUploader>
              </div>
              <div className="w-1/3 flex flex-col w-full gap-4 items-start">
                <Input
                  value={introTitle}
                  onChange={(e) => setIntroTitle(e.target.value)}
                  placeholder="Titre de la landing page"
                  className="border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-slate-400"
                />
                <Textarea
                  value={introDescription}
                  onChange={(e) => setIntroDescription(e.target.value)}
                  placeholder="Description de la landing page"
                  className="min-h-[180px]"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t border-slate-200 bg-white px-6 py-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-lg border-slate-300"
            disabled={isSaving || isUploadingImage}
          >
            {t("Annuler")}
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            className="min-w-[140px] rounded-lg"
            disabled={
              isSaving ||
              isUploadingImage ||
              !newName.trim() ||
              !selectedProjectTypeId ||
              !newDateFin ||
              (hasLaunchDate &&
                (!newLaunchDate ||
                  !newExpectedDuration ||
                  Number(newExpectedDuration) <= 0)) ||
              !selectedCountryId
            }
          >
            {isSaving ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                {t("... sauvegarde en cours")}
              </>
            ) : (
              t("Sauvegarder")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function ProgrammeBlock({
  callForProject,
  isOpen,
  setOpenProgramme,
  t,
  columns,
  setCallList,
  onDeleteCall,
}) {
  // const { t } = useTranslation("dashboard", {
  //   keyPrefix: "soustraitant",
  // });

  // const columns = getColumns();
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingDeleteCall, setLoadingDeleteCall] = useState(false);
  const [open, setOpen] = useState(false);
  const [openCandidateDelete, setOpenCandidateDelete] = useState(false);
  const [candidateDeleteIds, setCandidateDeleteIds] = useState<number[]>([]);
  const [deleteCandidateDocuments, setDeleteCandidateDocuments] =
    useState(false);
  const [deleteCallDocuments, setDeleteCallDocuments] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [isEditProgramme, setIsEditProgramme] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "score", desc: false },
  ]);
  const [candidateSearch, setCandidateSearch] = useState("");
  const [candidateStatusFilter, setCandidateStatusFilter] = useState("all");
  const [loadingSaveName, setLoadingSaveName] = useState(false);
  const navigate = useNavigate();
  const { typesProject } = useTypeProjectContext();
  const { user, updateUserState } = useAuth();
  const hidePostActions = useHidePostActions();
  const hasUsableForm = Boolean(callForProject?.hasUsableForm);
  const [shareMetaCode, setShareMetaCode] = useState<string | null>(
    user?.codeMeta ?? null,
  );
  const [isPreparingShareLink, setIsPreparingShareLink] = useState(false);

  const { postData } = usePostData();
  const [form, setForm] = useState({
    nom: "",
    prenom: "",
    email: "",
    idProgramme: callForProject.idProgramme,
    idCall: callForProject.id,
  });
  const [errors, setErrors] = useState({});
  useEffect(() => {
    setShareMetaCode(user?.codeMeta ?? null);
  }, [user?.codeMeta]);

  const handleChange = (e: any) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!form.nom.trim()) newErrors.nom = "Le nom est requis.";
    if (!form.prenom.trim()) newErrors.prenom = "Le prénom est requis.";

    if (!form.email.trim()) {
      newErrors.email = "L'email est requis.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Format d'email invalide.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const confirmExistingIntervenantAccount = async () => {
    const normalizedEmail = form.email.trim().toLowerCase();
    const alreadyLinkedCandidate = callForProject.intervenants.some(
      (intervenant) => intervenant.email?.trim().toLowerCase() === normalizedEmail,
    );

    if (alreadyLinkedCandidate) {
      toast({
        className: "text-red-500",
        title: "Candidat déjà ajouté",
        description:
          "Un candidat utilise déjà cette adresse email pour cet appel à projets.",
        duration: 4000,
      });
      return false;
    }

    const token =
      localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_ENDPOINT}/check-existing-user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            email: form.email,
            targetRole: "ROLE_CPINTERVENANT",
          }),
        },
      );

      const result = await response.json().catch(() => null);
      if (!response.ok) {
        toast({
          className: "text-red-500",
          title: "Error",
          description:
            result?.message || "Cette adresse email est déjà utilisée.",
          duration: 4000,
        });
        return false;
      }

      if (!result?.existingUser) {
        return true;
      }

      return window.confirm(
        "Cette adresse email est déjà utilisée comme Porteur, financeur ou autre rôle. Voulez-vous aussi rattacher ce compte comme intervenant de l'appel à projet ? Les données nom, prénom et organisation ne seront pas modifiées. Un nouveau code d'accès sera envoyé par email.",
      );
    } catch {
      return true;
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    const confirmedExistingAccount = await confirmExistingIntervenantAccount();
    if (!confirmedExistingAccount) {
      setIsLoading(false);
      return;
    }

    const optimisticIntervenantId = `optimistic-candidate-${Date.now()}`;
    const optimisticIntervenant = {
      id: optimisticIntervenantId,
      nom: form.nom,
      prenom: form.prenom,
      email: form.email,
      codeAcess: "",
      link: "",
      scoringEnabled: Boolean(callForProject.scoringEnabled),
      project: null,
    } as Intervenant;

    setCallList((prev: any) =>
      prev.map((call: any) => {
        if (
          call.id === form.idCall &&
          call.idProgramme === form.idProgramme
        ) {
          const updatedIntervenants = [
            ...call.intervenants,
            optimisticIntervenant,
          ];
          return {
            ...call,
            candidateCount: updatedIntervenants.length,
            intervenants: updatedIntervenants,
          };
        }
        return call;
      }),
    );
    setShowForm(false);
    setForm({
      nom: "",
      prenom: "",
      email: "",
      idProgramme: callForProject.idProgramme,
      idCall: callForProject.id,
    });

    const urlApi = `${import.meta.env.VITE_IP_API}/api/add-new-intervenant`;
    const res = await postData({
      url: urlApi,
      data: {
        ...form,
        confirmExistingUser: true,
      },
    });

    if (res && res.success) {
      const newData = res.intervenant;
      setCallList((prev: any) =>
        prev.map((call: any) => {
          if (
            call.id === form.idCall &&
            call.idProgramme === form.idProgramme
          ) {
            const updatedIntervenants = [
              ...call.intervenants.filter(
                (intervenant: any) =>
                  intervenant.id !== optimisticIntervenantId,
              ),
              {
                id: String(newData.id),
                nom: newData.intervenant.lastname,
                prenom: newData.intervenant.firstname,
                email: newData.intervenant.email,
                codeAcess: newData.codeAccess,
                link:
                  window.location.origin +
                  "/authentification-cfp/" +
                  newData.link +
                  "/" +
                  slugify(
                    newData.intervenant.lastname +
                    newData.intervenant.firstname,
                  ),
              } as Intervenant,
            ];

            return {
              ...call,
              candidateCount: updatedIntervenants.length,
              intervenants: updatedIntervenants,
            };
          }
          return call;
        }),
      );

      toast({
        title: "Success",
        description: t("Intervenant ajouté avec succès"),
        duration: 4000,
      });
      setIsLoading(false);
    } else {
      setCallList((prev: any) =>
        prev.map((call: any) => {
          if (
            call.id === callForProject.id &&
            call.idProgramme === callForProject.idProgramme
          ) {
            const updatedIntervenants = call.intervenants.filter(
              (intervenant: any) =>
                intervenant.id !== optimisticIntervenantId,
            );
            return {
              ...call,
              candidateCount: updatedIntervenants.length,
              intervenants: updatedIntervenants,
            };
          }
          return call;
        }),
      );
      setIsLoading(false);
      toast({
        className: "text-red-500",
        title: "Error",
        description:
          "L'adresse e-mail est déjà utilisé par un autre utilisateur",
        duration: 4000,
      });
    }
  };

  const handleDelete = (
    appelId: number,
    ids: number[],
    deleteDocuments = false,
  ) => {
    setLoadingDelete(true);
    const idsStr = ids.map(String);
    const previousIntervenants = callForProject.intervenants;

    setCallList((prev: any) =>
      prev.map((p: any) => {
        if (String(p.id) === String(appelId)) {
          const newIntervenants = p.intervenants.filter(
            (i: any) => !idsStr.includes(String(i.id)),
          );

          return {
            ...p,
            candidateCount: newIntervenants.length,
            intervenants: newIntervenants,
          };
        }
        return p;
      }),
    );
    setOpenCandidateDelete(false);
    setCandidateDeleteIds([]);
    setDeleteCandidateDocuments(false);

    postData({
      url: import.meta.env.VITE_API_ENDPOINT + "/remove-intervenant",
      data: { appelId, ids, deleteDocuments },
    })
      .then((res) => {
        if (res?.success) {
          setLoadingDelete(false);
          toast({
            title: t("Les intervenants ont bien été supprimés"),
          });
          return;
        }

        setCallList((prev: any) =>
          prev.map((p: any) =>
            String(p.id) === String(appelId)
              ? {
                  ...p,
                  candidateCount: previousIntervenants.length,
                  intervenants: previousIntervenants,
                }
              : p,
          ),
        );
      })
      .finally(() => setLoadingDelete(false));
  };

  const handleDeleteCall = () => {
    setLoadingDeleteCall(true);
    const idCallProgram = callForProject.id;
    const deleteDocuments = deleteCallDocuments;
    setDeleteCallDocuments(false);
    Promise.resolve(onDeleteCall?.(idCallProgram, deleteDocuments)).finally(() =>
      setLoadingDeleteCall(false),
    );
  };

  const filteredIntervenants = useMemo(() => {
    const normalizedSearch = candidateSearch.trim().toLowerCase();

    return callForProject.intervenants.filter((intervenant) => {
      const matchesSearch =
        normalizedSearch === ""
        || [intervenant.nom, intervenant.prenom, intervenant.email]
          .some((value) => String(value ?? "").toLowerCase().includes(normalizedSearch));

      if (!matchesSearch) {
        return false;
      }

      if (candidateStatusFilter === "all") {
        return true;
      }

      const displayedStatus = getDisplayedCandidateStatus(
        intervenant.project,
        intervenant.scoringEnabled,
      );

      if (candidateStatusFilter === "not-started") {
        return displayedStatus === 0 || displayedStatus === 7;
      }

      if (candidateStatusFilter === "awaiting-validation") {
        return displayedStatus === 2 || displayedStatus === 6;
      }

      return String(displayedStatus) === candidateStatusFilter;
    });
  }, [
    callForProject.intervenants,
    candidateSearch,
    candidateStatusFilter,
  ]);

  const table = useReactTable({
    data: filteredIntervenants,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
    },
  });

  const ensureShareMetaCode = async (): Promise<string | null> => {
    if (shareMetaCode) {
      return shareMetaCode;
    }

    if (!user?.id) {
      toast({
        title: "Lien de partage indisponible",
        description: "Impossible de générer le lien de partage pour le moment.",
      });
      return null;
    }

    setIsPreparingShareLink(true);
    try {
      const response = await postData({
        url: `${import.meta.env.VITE_API_ENDPOINT}/user/generate-metacode/${user.id}`,
        data: {},
      });

      const generatedCode =
        typeof response?.codeMeta === "string" &&
          response.codeMeta.trim() !== ""
          ? response.codeMeta.trim()
          : null;

      if (!generatedCode) {
        toast({
          title: "Lien de partage indisponible",
          description: "Le code de partage n'a pas pu être généré.",
        });
        return null;
      }

      setShareMetaCode(generatedCode);
      if (response?.id) {
        updateUserState(response as User);
      }
      return generatedCode;
    } finally {
      setIsPreparingShareLink(false);
    }
  };

  const linkProgram =
    window.location.origin +
    "/submit/cfp-subscription/" +
    (shareMetaCode ?? "") +
    "/pgm-" +
    callForProject.id;

  const handleCopyLink = async () => {
    const resolvedMetaCode = await ensureShareMetaCode();
    if (!resolvedMetaCode) return;

    const resolvedLink =
      window.location.origin +
      "/submit/cfp-subscription/" +
      resolvedMetaCode +
      "/pgm-" +
      callForProject.id;

    navigator.clipboard.writeText(resolvedLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleOpenShare = async () => {
    const resolvedMetaCode = await ensureShareMetaCode();
    if (!resolvedMetaCode) return;
    setIsShareOpen(true);
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      await downloadCallForProjectExcel(callForProject.id);
      setIsExportDialogOpen(false);
      toast({
        title: "Export Excel généré",
        description: "Le rapport a bien été téléchargé.",
      });
    } catch (_error) {
      toast({
        title: "Export impossible",
        description:
          "Une erreur est survenue pendant la génération du fichier Excel.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportWord = async () => {
    setIsExporting(true);
    try {
      await downloadCallForProjectWord(callForProject.id);
      setIsExportDialogOpen(false);
      toast({
        title: "Export Word généré",
        description: "Le document a bien été téléchargé.",
      });
    } catch (_error) {
      toast({
        title: "Export impossible",
        description:
          "Une erreur est survenue pendant la génération du document Word.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveEditName = async (
    newProgramName: string,
    newIntroduction: string,
    projectTypeId: number | null,
    newDateFin: string,
    hasLaunchDate: boolean,
    newLaunchDate: string | null,
    newExpectedDuration: number | null,
    newCountryId: number | null,
  ) => {
    if (typeof projectTypeId !== "number" || !Number.isFinite(projectTypeId)) {
      toast({
        title: "Champ obligatoire",
        description: "Le type de projet est obligatoire.",
        duration: 2500,
      });
      return false;
    }
    if (!newDateFin) {
      toast({
        title: "Champ obligatoire",
        description: "La date de fin est obligatoire.",
        duration: 2500,
      });
      return false;
    }
    if (hasLaunchDate && !newLaunchDate) {
      toast({
        title: "Champ obligatoire",
        description: "La date de lancement est obligatoire.",
        duration: 2500,
      });
      return false;
    }
    if (
      hasLaunchDate &&
      (typeof newExpectedDuration !== "number" ||
        !Number.isFinite(newExpectedDuration) ||
        newExpectedDuration <= 0)
    ) {
      toast({
        title: "Champ obligatoire",
        description: "La durée prévue est obligatoire.",
        duration: 2500,
      });
      return false;
    }
    if (typeof newCountryId !== "number" || !Number.isFinite(newCountryId)) {
      toast({
        title: "Champ obligatoire",
        description: "Le pays est obligatoire.",
        duration: 2500,
      });
      return false;
    }

    setLoadingSaveName(true);
    const optimisticCall = {
      ...callForProject,
      name: newProgramName,
      introduction: newIntroduction,
      dateFin: newDateFin,
      hasLaunchDate,
      launchDate: hasLaunchDate ? newLaunchDate : null,
      expectedDuration: hasLaunchDate ? newExpectedDuration : null,
      country:
        typeof newCountryId === "number" && Number.isFinite(newCountryId)
          ? {
              id: newCountryId,
              name:
                callForProject.country?.id === newCountryId
                  ? callForProject.country?.name
                  : `Pays #${newCountryId}`,
            }
          : null,
      projectType:
        typeof projectTypeId === "number" && Number.isFinite(projectTypeId)
          ? {
              id: projectTypeId,
              name:
                typesProject?.find(
                  (tp: any) => Number(tp?.id) === projectTypeId,
                )?.name ??
                callForProject.projectType?.name ??
                `Type #${projectTypeId}`,
            }
          : null,
    };
    setCallList((prev: any) =>
      prev.map((call: any) =>
        String(call.id) === String(callForProject.id)
          ? optimisticCall
          : call,
      ),
    );
    setIsEditProgramme(false);

    const res = await postData({
      url: import.meta.env.VITE_API_ENDPOINT + "/update-call-for-project",
      data: {
        idCall: callForProject.id,
        newProgramName,
        newIntroduction,
        newProjectTypeId: projectTypeId,
        newDateFin,
        newHasLaunchDate: hasLaunchDate,
        newLaunchDate,
        newExpectedDuration,
        newCountryId,
      },
    });

    if (res?.success) {
      setCallList((prev: any) =>
        prev.map((call: any) => {
          if (call.id !== callForProject.id) return call;
          return {
            ...call,
            name: newProgramName,
            introduction: newIntroduction,
            dateFin: newDateFin,
            hasLaunchDate,
            launchDate: hasLaunchDate ? newLaunchDate : null,
            expectedDuration: hasLaunchDate ? newExpectedDuration : null,
            country:
              typeof newCountryId === "number" && Number.isFinite(newCountryId)
                ? {
                  id: newCountryId,
                  name:
                    res?.country?.name ??
                    call.country?.name ??
                    `Pays #${newCountryId}`,
                }
                : null,
            projectType:
              typeof projectTypeId === "number" &&
                Number.isFinite(projectTypeId)
                ? {
                  id: projectTypeId,
                  name:
                    res?.projectType?.name ??
                    typesProject?.find(
                      (tp: any) => Number(tp?.id) === projectTypeId,
                    )?.name ??
                    call.projectType?.name ??
                    `Type #${projectTypeId}`,
                }
                : null,
          };
        }),
      );
      setLoadingSaveName(false);
      setIsEditProgramme(false);
      toast({
        title: "Success",
        description: t("Modification du programme avec succès"),
        duration: 4000,
      });
      return true;
    }

    setCallList((prev: any) =>
      prev.map((call: any) =>
        String(call.id) === String(callForProject.id)
          ? callForProject
          : call,
      ),
    );
    setLoadingSaveName(false);
    return false;
  };

  function handleDivClick(idProgramme: any) {
    window.location.href = "/dashboard/program/" + idProgramme;
  }

  const isCallExpired = isCallExpiredByDate(callForProject?.dateFin);
  const statusCounts = Array.isArray(callForProject?.intervenants)
    ? buildDisplayedStatusCounts(
        callForProject.intervenants,
        callForProject?.scoringEnabled,
      )
    : {
        validated: Number(callForProject?.statusCounts?.validated ?? 0),
        awaitingValidation: Number(
          callForProject?.statusCounts?.awaitingValidation ?? 0,
        ),
        rejected: Number(callForProject?.statusCounts?.rejected ?? 0),
        inProgress: Number(callForProject?.statusCounts?.inProgress ?? 0),
        notStarted: Number(callForProject?.statusCounts?.notStarted ?? 0),
      };
  const candidateCount = Number(
    Array.isArray(callForProject?.intervenants)
      ? callForProject.intervenants.length
      : (callForProject?.candidateCount ?? 0),
  );
  const hasIntervenants = candidateCount > 0;
  const displayedDeadline = formatDisplayDate(callForProject?.dateFin);
  const hasProjectType = Boolean(callForProject?.projectType?.id);

  return (
    <>
      <div
        className={`overflow-hidden rounded-[20px] border ${isCallExpired
          ? "border-[#ead3d3] bg-[linear-gradient(180deg,#fff9f9_0%,#f8f1f1_100%)]"
          : "border-[#d8e3d3] bg-[linear-gradient(180deg,#fbfcfa_0%,#f4f7f2_100%)]"
          }`}
      >
        {/* Header accordéon */}
        <button
          className={`w-full px-5 py-3 text-left transition-colors ${isCallExpired ? "hover:bg-[#f4e7e7]" : "hover:bg-[#f0f5ed]"
            }`}
          onClick={() => setOpenProgramme(isOpen ? null : callForProject.id)}
        >
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[20px] bg-[linear-gradient(180deg,#ecf7e9_0%,#ddeed8_100%)] text-[#1d5214]">
                  <BriefcaseBusiness className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDivClick(callForProject.idProgramme);
                    }}
                    className="inline-flex cursor-pointer items-center gap-2 hover:text-[#1d5214]"
                  >
                    <h2 className="truncate text-[15px] font-semibold text-[#142a4f] transition-colors hover:text-[#1d5214] hover:underline md:text-[17px]">
                      {callForProject.name}
                    </h2>
                    {isCallExpired && (
                      <span className="rounded-full border border-red-200 bg-red-50 px-3 py-1 text-[11px] font-medium text-red-700">
                        Clôturé
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-[#6d7781]">
                    <span className="inline-flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5 text-[#7d8791]" />
                      <span>
                        {candidateCount} candidat{candidateCount > 1 ? "s" : ""}
                      </span>
                    </span>
                    {callForProject?.projectType?.name && (
                      <span className="inline-flex items-center gap-1.5">
                        <Shapes className="h-3.5 w-3.5 text-[#7d8791]" />
                        <span>{callForProject.projectType.name}</span>
                      </span>
                    )}
                    {displayedDeadline && (
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5 text-[#7d8791]" />
                        <span>Date limite : {displayedDeadline}</span>
                      </span>
                    )}
                  </div>
                  {!hasProjectType && (
                    <p className="mt-2 text-[12px] font-medium text-red-600">
                      Veuillez sélectionner un type de projet pour cet appel à
                      projet
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2 xl:pl-4">
              <div className="flex items-center gap-1 overflow-x-auto rounded-[18px] bg-white/70 p-1">
                <CallStatusMiniCard
                  icon={<CheckCircle2 className="h-5 w-5 text-emerald-600" />}
                  value={statusCounts.validated}
                  label="Validés"
                  className="bg-[linear-gradient(180deg,#f3fbf2_0%,#edf7eb_100%)] text-emerald-700"
                />
                <CallStatusMiniCard
                  icon={<XCircle className="h-5 w-5 text-red-500" />}
                  value={statusCounts.rejected}
                  label="Rejetés"
                  className="bg-[linear-gradient(180deg,#fff2f2_0%,#ffe9e9_100%)] text-red-700"
                />
                <CallStatusMiniCard
                  icon={<Clock3 className="h-5 w-5 text-sky-600" />}
                  value={statusCounts.awaitingValidation}
                  label="En attente"
                  className="bg-[linear-gradient(180deg,#f1f8ff_0%,#e8f3ff_100%)] text-sky-700"
                />
                <CallStatusMiniCard
                  icon={<Loader className="h-5 w-5 text-orange-500" />}
                  value={statusCounts.inProgress}
                  label="En cours"
                  className="border border-orange-200 bg-[linear-gradient(180deg,#fff7ef_0%,#fff2e4_100%)] text-[#4b3a1a]"
                />
              </div>

              {!hidePostActions && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#d2dccd] bg-white text-[#4a5c45] transition-colors hover:bg-[#edf4e9]"
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    onClick={(e) => e.stopPropagation()}
                    className="w-56"
                  >
                    <DropdownMenuItem onClick={() => setIsEditProgramme(true)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      {t("modifier")}
                    </DropdownMenuItem>
                    {!isCallExpired && (
                      <CallFormActionDialog
                        callId={callForProject.id}
                        programName={callForProject.name}
                        formMode={callForProject.formMode}
                        canChangeForm={Boolean(callForProject.canChangeForm)}
                        trigger={
                          <DropdownMenuItem
                            onSelect={(e) => e.preventDefault()}
                          >
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Modifier le formulaire
                          </DropdownMenuItem>
                        }
                      />
                    )}
                    <DropdownMenuItem
                      onClick={() =>
                        navigate(
                          `/dashboard/parameters?params=3&callId=${callForProject.id}`,
                        )
                      }
                    >
                      <Mail className="mr-2 h-4 w-4" />
                      Paramétrer les mails
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setIsExportDialogOpen(true)}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Exporter
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setOpen(true);
                      }}
                      disabled={loadingDeleteCall}
                      className="text-red-600 focus:text-red-700"
                    >
                      {loadingDeleteCall ? (
                        <Loader className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Trash className="mr-2 h-4 w-4" />
                      )}
                      {loadingDeleteCall
                        ? t("Suppression en cours")
                        : t("Supprimer")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <div className="flex h-9 w-9 items-center justify-center rounded-full border border-[#d2dccd] bg-white/85 text-[#50614b]">
                {isOpen ? (
                  <ChevronDown className="h-5 w-5" />
                ) : (
                  <ChevronRight className="h-5 w-5" />
                )}
              </div>
            </div>
          </div>
        </button>

        {/* Modale Share */}
        {isShareOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-96 relative">
              {/* Fermer */}
              <button
                onClick={() => setIsShareOpen(false)}
                className="absolute top-2 right-2 p-1 hover:bg-gray-200 rounded-full"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>

              <h3 className="text-lg font-semibold mb-4">Partager le lien</h3>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={linkProgram}
                  readOnly
                  className="flex-1 border rounded px-3 py-2 text-sm bg-gray-100"
                />
                <Button
                  onClick={() => void handleCopyLink()}
                  variant="default"
                  size="sm"
                  disabled={isPreparingShareLink}
                  className="px-3 py-2 text-white rounded hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isPreparingShareLink ? (
                    <Loader className="w-4 h-4 mr-3 animate-spin" />
                  ) : (
                    <Copy className="w-4 h-4 mr-3" />
                  )}
                  {copiedLink ? "Copié !" : "Copier"}
                </Button>
              </div>
            </div>
          </div>
        )}

        <EditProgramDialog
          isOpen={isEditProgramme}
          onOpenChange={setIsEditProgramme}
          programm={callForProject}
          onSave={handleSaveEditName}
          isSaving={loadingSaveName}
          t={t}
          user={user}
          typesProject={Array.isArray(typesProject) ? typesProject : []}
        />
        <ExportCallDialog
          isOpen={isExportDialogOpen}
          onOpenChange={setIsExportDialogOpen}
          onExportExcel={handleExportExcel}
          onExportWord={handleExportWord}
          isExporting={isExporting}
        />

        {/* Contenu accordéon */}
        {isOpen && (
          <div className="p-5">
            <div className="p-5 bg-white">
              <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="w-full md:max-w-md">
                  <Input
                    type="text"
                    value={candidateSearch}
                    onChange={(e) => setCandidateSearch(e.target.value)}
                    placeholder="Rechercher par nom, prénom ou email"
                    className="h-10 rounded-xl border-[#d6e2d2] bg-white"
                  />
                </div>
                <div className="w-full md:w-[240px]">
                  <select
                    value={candidateStatusFilter}
                    onChange={(e) => setCandidateStatusFilter(e.target.value)}
                    className="h-10 w-full rounded-xl border border-[#d6e2d2] bg-white px-3 text-sm text-slate-700 outline-none transition-colors focus:border-[#1d5214]"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="not-started">Non commencé</option>
                    <option value="1">En cours</option>
                    <option value="awaiting-validation">En attente de validation</option>
                    <option value="3">Validé</option>
                    <option value="4">Rejeté</option>
                  </select>
                </div>
              </div>
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="text-center py-5"
                      >
                        {"Aucun candidat"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Suppression en groupe */}
              <div className="flex justify-between items-center mt-4">
                <span className="text-sm text-gray-600">
                  {table.getFilteredSelectedRowModel().rows.length}{" "}
                  {t("sélectionné(s)")}
                </span>
                {!hidePostActions &&
                  table.getFilteredSelectedRowModel().rows.length > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        setCandidateDeleteIds(
                          table
                            .getFilteredSelectedRowModel()
                            .rows.map((r) => Number(r.original.id)),
                        );
                        setDeleteCandidateDocuments(false);
                        setOpenCandidateDelete(true);
                      }}
                      disabled={loadingDelete}
                    >
                      {loadingDelete ? (
                        <>
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          {t("Suppression en cours")}
                        </>
                      ) : (
                        <>
                          <Trash className="w-4 h-4 mr-1" />{" "}
                          {t("Supprimer sélection")}
                        </>
                      )}
                    </Button>
                  )}
              </div>
            </div>
            <div className="mt-6 flex justify-center">
              {!showForm ? (
                <div className="flex items-center gap-4">
                  {!hidePostActions &&
                    hasProjectType &&
                    hasUsableForm &&
                    !isCallExpired && (
                      <Button
                        variant="outline"
                        className="flex items-center gap-2 px-5 py-2 rounded-xl shadow-sm hover:shadow-md transition-all"
                        onClick={() => setShowForm(true)}
                      >
                        <UserPlus className="w-5 h-5" />
                        <span className="font-medium">
                          {t("Inviter un nouveau candidat")}
                        </span>
                      </Button>
                    )}
                  {!hidePostActions &&
                    hasProjectType &&
                    hasUsableForm &&
                    !isCallExpired && (
                      <Button
                        variant="outline"
                        className="flex items-center gap-2 rounded-xl px-5 py-2 shadow-sm transition-all hover:shadow-md"
                        onClick={() => void handleOpenShare()}
                        disabled={isPreparingShareLink}
                      >
                        {isPreparingShareLink ? (
                          <>
                            <Loader className="w-4 h-4 mr-2 animate-spin" />
                            {t("Préparation...")}
                          </>
                        ) : (
                          <>
                            <Share2 className="w-5 h-5" />
                            <span className="font-medium">{t("Partager")}</span>
                          </>
                        )}
                      </Button>
                    )}
                </div>
              ) : (
                !hidePostActions && (
                  <form
                    onSubmit={handleSubmit}
                    className="flex flex-wrap items-start gap-4 bg-white rounded-xl shadow-lg p-4"
                  >
                    {/* Champ Nom */}
                    <div className="flex flex-col">
                      <Input
                        type="text"
                        name="nom"
                        value={form.nom}
                        onChange={handleChange}
                        placeholder="Nom"
                        className={`w-40 ${errors.nom ? "border-red-500" : ""}`}
                      />
                      {errors.nom && (
                        <span className="text-red-500 text-xs mt-1">
                          {errors.nom}
                        </span>
                      )}
                    </div>

                    {/* Champ Prénom */}
                    <div className="flex flex-col">
                      <Input
                        type="text"
                        name="prenom"
                        value={form.prenom}
                        onChange={handleChange}
                        placeholder="Prénom"
                        className={`w-40 ${errors.prenom ? "border-red-500" : ""}`}
                      />
                      {errors.prenom && (
                        <span className="text-red-500 text-xs mt-1">
                          {errors.prenom}
                        </span>
                      )}
                    </div>

                    {/* Champ Email */}
                    <div className="flex flex-col">
                      <Input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="Email"
                        className={`w-60 ${errors.email ? "border-red-500" : ""}`}
                      />
                      {errors.email && (
                        <span className="text-red-500 text-xs mt-1">
                          {errors.email}
                        </span>
                      )}
                    </div>

                    {/* Boutons */}
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex items-center gap-2 px-4 py-2 rounded-lg shadow-sm hover:shadow-md"
                        onClick={() => {
                          setShowForm(false);
                          setErrors({});
                        }}
                      >
                        <XCircle className="w-4 h-4" />
                        {t("Annuler")}
                      </Button>

                      <Button
                        type="submit"
                        variant="default"
                        className="flex items-center gap-2 px-4 py-2 rounded-lg shadow-sm hover:shadow-md"
                        disabled={isLoading}
                      >
                        {isLoading === true ? (
                          <>
                            <Loader className="w-4 h-4 mr-2 animate-spin" />
                            {t("Envoi en cours")}
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            {t("Soumettre")}
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                )
              )}
            </div>
          </div>
        )}
      </div>

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setDeleteCallDocuments(false);
          }
        }}
      >
        <DialogContent className="max-w-md rounded-2xl shadow-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-800">
              {hasIntervenants
                ? t("Suppression impossible")
                : t("Confirmation de suppression")}
            </DialogTitle>
            <DialogDescription className="text-gray-600 mt-2">
              {hasIntervenants ? (
                <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-200 p-4 rounded-xl">
                  <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 mt-1">
                    {t(
                      "Veuillez d'abord supprimer tous les appels à projets liés à ce programme avant de pouvoir le supprimer.",
                    )}
                  </p>
                </div>
              ) : (
                t(
                  "Êtes-vous sûr de vouloir supprimer ce programme ? Cette action est irréversible.",
                )
              )}
            </DialogDescription>
          </DialogHeader>

          {!hasIntervenants && !hidePostActions && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <label className="flex items-start gap-3 text-sm text-slate-700">
                <Checkbox
                  checked={deleteCallDocuments}
                  onCheckedChange={(checked) =>
                    setDeleteCallDocuments(Boolean(checked))
                  }
                />
                <span>
                  {
                    "Supprimer aussi les documents liés aux projets de cet appel à projets"
                  }
                </span>
              </label>
            </div>
          )}

          {/* Footer de la modale */}
          <DialogFooter className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
                setDeleteCallDocuments(false);
              }}
              className="rounded-lg"
            >
              {t("Annuler")}
            </Button>

            {!hasIntervenants && !hidePostActions && (
              <Button
                variant="destructive"
                onClick={() => {
                  setOpen(false);
                  handleDeleteCall();
                }}
                className="rounded-lg"
                disabled={loadingDeleteCall}
              >
                {loadingDeleteCall ? t("Suppression en cours") : t("Confirmer")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openCandidateDelete}
        onOpenChange={(nextOpen) => {
          setOpenCandidateDelete(nextOpen);
          if (!nextOpen) {
            setCandidateDeleteIds([]);
            setDeleteCandidateDocuments(false);
          }
        }}
      >
        <DialogContent className="max-w-md rounded-2xl shadow-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-800">
              Confirmation de suppression
            </DialogTitle>
            <DialogDescription className="text-gray-600 mt-2">
              {candidateDeleteIds.length > 1
                ? "Êtes-vous sûr de vouloir supprimer ces candidats ? Cette action est irréversible."
                : "Êtes-vous sûr de vouloir supprimer ce candidat ? Cette action est irréversible."}
            </DialogDescription>
          </DialogHeader>

          {!hidePostActions && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <label className="flex items-start gap-3 text-sm text-slate-700">
                <Checkbox
                  checked={deleteCandidateDocuments}
                  onCheckedChange={(checked) =>
                    setDeleteCandidateDocuments(Boolean(checked))
                  }
                />
                <span>
                  Supprimer aussi les documents du projet lié à ce candidat
                </span>
              </label>
            </div>
          )}

          <DialogFooter className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setOpenCandidateDelete(false);
                setCandidateDeleteIds([]);
                setDeleteCandidateDocuments(false);
              }}
              className="rounded-lg"
            >
              {t("Annuler")}
            </Button>

            {!hidePostActions && (
              <Button
                variant="destructive"
                onClick={() => {
                  handleDelete(
                    callForProject.id,
                    candidateDeleteIds,
                    deleteCandidateDocuments,
                  );
                }}
                className="rounded-lg"
                disabled={loadingDelete || candidateDeleteIds.length === 0}
              >
                {loadingDelete ? t("Suppression en cours") : t("Confirmer")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const CallForProjects: React.FC = () => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "soustraitant",
  });
  const [isLoading, setIsloading] = useState(true);
  const [data, setData] = useState<Intervenant[]>([]);
  const [isGlobalExportDialogOpen, setIsGlobalExportDialogOpen] =
    useState(false);
  const [isGlobalExporting, setIsGlobalExporting] = useState(false);

  const [callList, setCallList] = useState<CallForProject[]>([]);
  const [openProgramme, setOpenProgramme] = useState<number | null>(null);
  const { postData } = usePostData();
  const hidePostActions = useHidePostActions();

  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const { user, updateUserState } = useAuth();
  const [codeMeta, setCodeMeta] = useState("");
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const queryClient = useQueryClient();
  const callsForProjectsEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/call-for-project/all`;
  const callsForProjectsCacheKey = [
    dashboardQueryKeys.callForProjects,
    callsForProjectsEndpoint,
  ];

  const callsForProjectsData = useFetchDataV2(
    callsForProjectsEndpoint,
    {
      browserCache: true,
      browserCacheKey: "call-for-project-all-v2",
      browserCacheNamespace: "dashboard",
      browserCacheTtlMs: 2 * 60 * 1000,
      cacheId: dashboardQueryKeys.callForProjects,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
    },
  );

  const updateCallsCache = (
    updater: (current: CallForProject[] | undefined) => CallForProject[] | undefined,
  ) => {
    queryClient.setQueriesData(
      {
        exact: false,
        queryKey: [dashboardQueryKeys.callForProjects],
      },
      (current: any) => {
        const normalized = Array.isArray(current)
          ? current.map(normalizeCallForProject)
          : undefined;
        return updater(normalized);
      },
    );
  };

  const updateCallList = (
    updater:
      | CallForProject[]
      | ((current: CallForProject[]) => CallForProject[]),
  ) => {
    setCallList((current) => {
      const next =
        typeof updater === "function"
          ? (updater as (current: CallForProject[]) => CallForProject[])(current)
          : updater;
      queryClient.setQueryData(callsForProjectsCacheKey, next);
      updateCallsCache(() => next);
      return next;
    });
  };

  const snapshotCallsQueries = () =>
    queryClient.getQueriesData<CallForProject[]>({
      exact: false,
      queryKey: [dashboardQueryKeys.callForProjects],
    });

  const restoreCallsQueries = (
    snapshots: [unknown, CallForProject[] | undefined][],
  ) => {
    snapshots.forEach(([queryKey, previousData]) => {
      queryClient.setQueryData(queryKey as any, previousData);
    });
  };

  const applyOptimisticCallCreate = (draftCall: any) => {
    const normalizedDraft = normalizeCallForProject(draftCall);
    const snapshots = snapshotCallsQueries();
    updateCallList((current) => [normalizedDraft, ...current]);
    setOpenProgramme(normalizedDraft.id);
    return { snapshots, optimisticId: normalizedDraft.id };
  };

  const upsertCallInCache = (call: any, optimisticId?: number | string) => {
    const nextCall = normalizeCallForProject(call);
    updateCallList((current) => {
      const exists = current.some(
        (item) =>
          String(item.id) === String(nextCall.id) ||
          String(item.id) === String(optimisticId),
      );
      return exists
        ? current.map((item) =>
            String(item.id) === String(nextCall.id) ||
            String(item.id) === String(optimisticId)
              ? { ...item, ...nextCall }
              : item,
          )
        : [nextCall, ...current];
    });
    setOpenProgramme(nextCall.id);
  };

  const rollbackCalls = (meta: any) => {
    if (Array.isArray(meta?.snapshots)) {
      restoreCallsQueries(meta.snapshots);
      const restored = queryClient.getQueryData<CallForProject[]>(
        callsForProjectsCacheKey,
      );
      setCallList(Array.isArray(restored) ? restored.map(normalizeCallForProject) : []);
    }
  };

  const handleDeleteCallOptimistic = async (
    idCallProgram: number | string,
    deleteDocuments = false,
  ) => {
    const snapshots = snapshotCallsQueries();
    updateCallList((current) =>
      current.filter((call) => String(call.id) !== String(idCallProgram)),
    );
    if (String(openProgramme) === String(idCallProgram)) {
      setOpenProgramme(null);
    }

    const res = await postData({
      url: import.meta.env.VITE_API_ENDPOINT + "/delete-call-for-project",
      data: { idCallProgram, deleteDocuments },
    });

    if (res?.success) {
      toast({
        title: t("L'appel à projets a bien été supprimé"),
      });
      return;
    }

    restoreCallsQueries(snapshots);
    const restored = queryClient.getQueryData<CallForProject[]>(
      callsForProjectsCacheKey,
    );
    setCallList(Array.isArray(restored) ? restored.map(normalizeCallForProject) : []);
  };

  useEffect(() => {
    if (callsForProjectsData?.data !== undefined) {
      setCallList(
        callsForProjectsData?.data?.length
          ? callsForProjectsData.data
              .filter(Boolean)
              .map(normalizeCallForProject)
          : ([] as CallForProject[]),
      );
      setIsloading(false);
    }
  }, [callsForProjectsData?.data]);

  useEffect(() => {
    /*setIsloading(false);
    setProgrammes(initialProgrammes);*/
  }, []);

  useEffect(() => {
    if (user) {
      setIsUserLoaded(true);
    }
    if (user?.codeMeta) {
      setCodeMeta(user?.codeMeta);
    }
  }, [user]);

  const onOpenChange = (isOpen: boolean) => {
    setOpenModalDelete(isOpen);
  };

  const handleExportGlobalExcel = async () => {
    setIsGlobalExporting(true);
    try {
      await downloadGlobalCallForProjectsExcel();
      setIsGlobalExportDialogOpen(false);
      toast({
        title: "Export global Excel généré",
        description: "Le rapport consolidé a bien été téléchargé.",
      });
    } catch (_error) {
      toast({
        title: "Export impossible",
        description:
          "Une erreur est survenue pendant la génération du rapport global Excel.",
        variant: "destructive",
      });
    } finally {
      setIsGlobalExporting(false);
    }
  };

  const handleExportGlobalWord = async () => {
    setIsGlobalExporting(true);
    try {
      await downloadGlobalCallForProjectsWord();
      setIsGlobalExportDialogOpen(false);
      toast({
        title: "Export global Word généré",
        description: "Le rapport global a bien été téléchargé.",
      });
    } catch (_error) {
      toast({
        title: "Export impossible",
        description:
          "Une erreur est survenue pendant la génération du rapport global Word.",
        variant: "destructive",
      });
    } finally {
      setIsGlobalExporting(false);
    }
  };

  // Supprimer des intervenants sélectionnés
  const handleDeleteProgramme = (programmeId: number, ids: number[]) => {
    setLoadingDelete(true);
    postData({
      url: import.meta.env.VITE_API_ENDPOINT + "/remove-intervenant",
      data: {
        programmeId,
        ids,
      },
    }).then((res) => {
      if (res.success !== undefined && res.success) {
        setCallList((prev) =>
          prev.map((p) =>
            p.id === programmeId
              ? {
                ...p,
                intervenants: p.intervenants.filter(
                  (i) => !ids.includes(i.id),
                ),
              }
              : p,
          ),
        );
        onOpenChange(false);
        toast({
          title: t("Les intervenants ont bien été supprimés"),
        });
      }

      setLoadingDelete(false);
    });
    /*setCallList((prev) =>
      prev.map((p) =>
        p.id === programmeId
          ? { ...p, intervenants: p.intervenants.filter((i) => !ids.includes(i.id)) }
          : p
      )
    );*/
  };

  // Colonnes du tableau
  const getColumns = (): ColumnDef<Intervenant>[] => [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "nom",
      header: "Nom",
      cell: ({ row }) => <div>{row.getValue("nom")}</div>,
    },
    {
      accessorKey: "prenom",
      header: "Prénom",
      cell: ({ row }) => <div>{row.getValue("prenom")}</div>,
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => <div>{row.getValue("email")}</div>,
    },
    {
      id: "project",
      header: "Projet",
      cell: ({ row }) => {
        const project = row.original.project;
        if (!project?.id) {
          return <span className="text-sm text-slate-400">-</span>;
        }
        const projectLink = buildCandidateProjectDashboardLink(project);

        return (
          <button
            type="button"
            onClick={() => {
              window.location.href = projectLink;
            }}
            className="max-w-[220px] truncate text-left text-sm font-medium text-[#1d5214] transition-colors hover:text-[#163d10] hover:underline"
            title={project.name}
          >
            {project.name}
          </button>
        );
      },
    },
    {
      id: "projectStatus",
      header: "Statut",
      cell: ({ row }) => {
        const project = row.original.project;
        const badge = getProjectStatusBadgeConfig(
          project?.status,
          project,
          row.original.scoringEnabled,
        );
        return (
          <span
            className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${badge.className}`}
          >
            {badge.label}
          </span>
        );
      },
    },
    {
      id: "score",
      accessorFn: (row) => row.project?.scorePercentage ?? undefined,
      sortUndefined: "last",
      header: ({ column }) => {
        const sorted = column.getIsSorted();

        return (
          <button
            type="button"
            onClick={() => column.toggleSorting(sorted === "asc")}
            className="inline-flex items-center gap-2 font-medium text-slate-700 transition-colors hover:text-[#1d5214]"
          >
            <span>Score</span>
            <ChevronsUpDown className="h-4 w-4 text-slate-400" />
          </button>
        );
      },
      cell: ({ row }) => {
        const project = row.original.project;
        const scoreDisplay = formatCandidateScoreDisplay(
          project,
          row.original.scoringEnabled,
        );
        return (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700">
            <span>{scoreDisplay.label}</span>
            {/* {scoreDisplay.awaitingEvaluation && (
              <span
                className="inline-flex items-center text-sky-600"
                title="En attente d'evaluation"
                aria-label="En attente d'evaluation"
              >
                <InfoCircledIcon className="h-4 w-4" />
              </span>
            )} */}
          </span>
        );
      },
    },
    {
      id: "access",
      header: "Accès",
      cell: ({ row }) => (
        <CandidateAccessCell
          link={String(row.original.link ?? "")}
          codeAccess={String(row.original.codeAcess ?? "")}
        />
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const project = row.original.project;
        if (!project?.id) {
          return <span className="text-sm text-slate-400">-</span>;
        }
        const projectLink = buildCandidateProjectDashboardLink(project);

        return (
          <button
            type="button"
            onClick={() => {
              window.location.href = projectLink;
            }}
            className="inline-flex items-center gap-2 rounded-full border border-[#cfe0c7] bg-[linear-gradient(180deg,#f7fbf5_0%,#edf6e8_100%)] px-3 py-1.5 text-sm font-medium text-[#1d5214] shadow-sm transition-all hover:border-[#b9d0ae] hover:bg-[linear-gradient(180deg,#f1f8ed_0%,#e5f1df_100%)] hover:text-[#163d10] hover:shadow"
          >
            <Eye className="h-4 w-4" />
            <span>Voir</span>
          </button>
        );
      },
    },
  ];
  const displayedCallList = useMemo(
    () =>
      callList.length > 0
        ? callList
        : Array.isArray(callsForProjectsData?.data)
          ? callsForProjectsData.data.map(normalizeCallForProject)
          : [],
    [callList, callsForProjectsData?.data],
  );
  const displayLoading =
    isLoading && displayedCallList.length === 0 && callsForProjectsData.loading;

  return (
    <div>
      <ReactBreadcrumb
        items={[
          {
            href: ``,
            name: t("Mes Appels à Projets"),
          },
        ]}
        isTruncated={false}
      />

      <div className="flex flex-wrap gap-5 items-center justify-between mt-5">
        <h1
          className="mb-2 mt-4 text-[24px] font-medium inline-block"
          id="program-title"
        >
          {t("Mes Appels à Projets")}
        </h1>

        <div className="flex items-center gap-3">
          {!hidePostActions && !displayLoading && displayedCallList.length > 0 && (
            <Button
              type="button"
              onClick={() => setIsGlobalExportDialogOpen(true)}
              className="rounded-2xl border border-[#d6e2d2] !bg-transparent bg-[linear-gradient(135deg,#ffffff_0%,#f2f7ef_100%)] px-5 py-2.5 text-[#22421d] shadow-sm transition-all hover:!border-[#1d5214] hover:!bg-[#1d5214] hover:!bg-none hover:!text-white hover:shadow-md focus-visible:ring-[#1d5214]"
            >
              <Download className="mr-2 h-4 w-4" />
              Exporter
            </Button>
          )}
          {!hidePostActions && (
            <GenerateNewLink
              setData={setData}
              onOptimisticCreate={applyOptimisticCallCreate}
              onCreateSuccess={(createdCall, meta) => {
                upsertCallInCache(createdCall, meta?.optimisticId);
              }}
              onCreateError={rollbackCalls}
            />
          )}
        </div>
      </div>
      <div className="mt-4 space-y-5">
        {displayLoading && <TableSkeleton />}

        {!displayLoading &&
          displayedCallList.length > 0 &&
          displayedCallList.map((call) => {
            const columns = getColumns();
            const isOpen = openProgramme === call.id;

            return (
              <ProgrammeBlock
                key={call.id}
                callForProject={call}
                isOpen={isOpen}
                setOpenProgramme={setOpenProgramme}
                t={t}
                columns={columns}
                setCallList={updateCallList}
                onDeleteCall={handleDeleteCallOptimistic}
              />
            );
          })}

        {!displayLoading && displayedCallList.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 bg-gray-50 rounded-2xl shadow-inner">
            <svg
              className="w-20 h-20 text-gray-400 mb-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-gray-700">
              {t("Aucune donnée disponible")}
            </h3>
            <p className="text-gray-500 mt-2 text-center max-w-sm">
              {t(
                "Veuillez ajouter un prgramme pour appel à projet pour commencer.",
              )}
            </p>
          </div>
        )}
      </div>
      <GlobalExportDialog
        isOpen={isGlobalExportDialogOpen}
        onOpenChange={setIsGlobalExportDialogOpen}
        onExportExcel={handleExportGlobalExcel}
        onExportWord={handleExportGlobalWord}
        isExporting={isGlobalExporting}
      />
    </div>
  );
};

export default CallForProjects;
